"use client";

import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
} from "@ionic/react";
import { AttachMoney, Receipt, PersonAdd, Close, Person, Search, CurrencyBitcoin, ReceiptLong, TrendingUp, Cancel } from "@mui/icons-material";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Avatar,
  IconButton,
  InputAdornment,
  Autocomplete,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { createCustomer, searchCustomers, searchCustomersLive } from "../../../api/customers/customers";
import { createCustomerOrder_Crypto, createCustomerOrder_giftCard, getCustomerBanks, getExternalProcessorsAPI, verifyBankAccount } from "../../../api/orders/orders";
import { banks } from "../../../api/banks";
import {
  TrendingDown,
  AccountBalance,
  AttachFile,
  Description,
  Visibility,
  CheckCircle
} from '@mui/icons-material';
import { getCryptoCurrenciesAPI } from "../../../api/orders/orders"; // Import the actual API function
import { MenuButton } from "../../../components/Menu";
import { Layout } from "lucide-react";

// Helper function to safely convert to number
const toNumber = (value: any): number => {
  if (value === null || value === undefined || value === '') return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

// Helper function to safely check if a string is empty
const isStringEmpty = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value !== 'string') return true;
  return value.trim() === '';
};

// Mock data for currencies in case of API error (based on your table structure)
const mockCryptoCurrencies = [
  { id: 13, name: "Bitcoin", symbol: "BTC", business_id: "bus_id_b414c8dd64817f8c9a3e60f9bad83c86", hasRateBox: true, rateBoxLabel: "BTC", dateCreated: "2025-10-30 12:22:48" },
  { id: 14, name: "Etherum", symbol: "ETH", business_id: "bus_id_b414c8dd64817f8c9a3e60f9bad83c86", hasRateBox: true, rateBoxLabel: "ETH", dateCreated: "2025-10-30 12:23:07" },
  { id: 15, name: "USDT", symbol: "USDT", business_id: "bus_id_b414c8dd64817f8c9a3e60f9bad83c86", hasRateBox: false, rateBoxLabel: "usdt", dateCreated: "2025-10-30 12:23:21" },
];

const initialFormData = {
  cryptoType: "BTC",
  cryptoAmount: "",
  cryptoValue: "", // New field for crypto value
  sellingRate: "",
  costRate: "",
  customerName: "",
  customerPhone: "",
  customerEmail: "",
  username: "",
  customer_bank_code: "",
  customer_account_number: "",
  customer_account_name: "",
  transactionStartTime: "",
  external_processor: "",
};

const PosPage_Crypto: React.FC = () => {
    const history = useHistory();
    const [formData, setFormData] = useState(initialFormData);
    const [transactionStartTime, setTransactionStartTime] = useState<Date | null>(new Date());
    const [transactionEndTime, setTransactionEndTime] = useState<Date | null>(new Date());
    const [uploadedFile, setUploadedFile] = useState<File | null>(null);
    const [jobStatsFile, setJobStatsFile] = useState<File | null>(null);
    
    const [sellingPrice, setSellingPrice] = useState(0);
    const [costPrice, setCostPrice] = useState(0);
    const [commission, setCommission] = useState(0);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showCustomerDialog, setShowCustomerDialog] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        firstName: "",
        middleName: "",
        lastName: "",
        phone: "",
        email: "",
        grade: "A",
        altEmail: "",
    });
    const [loading, setLoading] = useState({
        customers: false,
        grades: false,
        products: false,
        externalProcessors: false,
        cryptoCurrencies: false, // New loading state for crypto currencies
    });
    const [processingOrder, setProcessingOrder] = useState(false);
    const [verifyingAccount, setVerifyingAccount] = useState(false);
    const [showSummaryDialog, setShowSummaryDialog] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [customerBanks, setCustomerBanks] = useState([]);
    const [selectedBankId, setSelectedBankId] = useState("");
    
    const [externalProcessors, setExternalProcessors] = useState([]);
    const [cryptoCurrencies, setCryptoCurrencies] = useState([]); // New state for crypto currencies
    const [selectedCryptoCurrency, setSelectedCryptoCurrency] = useState(null); // Track selected crypto

    const [allCustomers, setAllCustomers] = useState([]);
    const [customersFetched, setCustomersFetched] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);
    const [formErrors, setFormErrors] = useState<string[]>([]);

    // Helper to check if hasRateBox is effectively true
    const hasRateBoxEffectively = (crypto: any) => {
        if (!crypto) return false;
        return crypto.hasRateBox === true || crypto.hasRateBox === 'true';
    };

    // Validation function to check if all required fields are filled
    const validateForm = () => {
        const errors: string[] = [];
        
        // Customer validation
        if (!selectedCustomer) {
            errors.push("Please select a customer");
        }
        
        // Crypto info validation
        if (!formData.cryptoType) {
            errors.push("Crypto type is required");
        }
        if (!formData.cryptoAmount || toNumber(formData.cryptoAmount) <= 0) {
            errors.push("Crypto amount is required and must be greater than 0");
        }
        
        // Crypto value validation for currencies that require it
        if (hasRateBoxEffectively(selectedCryptoCurrency) && isStringEmpty(formData.cryptoValue)) {
            errors.push(`Crypto value is required for ${formData.cryptoType}`);
        }
        
        // Rate validation
        if (!formData.costRate || toNumber(formData.costRate) <= 0) {
            errors.push("Cost rate is required and must be greater than 0");
        }
        if (!formData.sellingRate || toNumber(formData.sellingRate) <= 0) {
            errors.push("Selling rate is required and must be greater than 0");
        }
        
        // Customer bank info validation
        if (!formData.customer_bank_code) {
            errors.push("Customer bank is required");
        }
        if (!formData.customer_account_number || formData.customer_account_number.length !== 10) {
            errors.push("Customer account number must be 10 digits");
        }
        if (isStringEmpty(formData.customer_account_name)) {
            errors.push("Customer account name is required");
        }
        
        // Transaction time validation
        if (!transactionStartTime) {
            errors.push("Transaction start time is required");
        }
        
        // Commission validation (must be at least ₦100)
        if (commission < 100) {
            errors.push("Commission must be at least ₦100");
        }
        
        setFormErrors(errors);
        return errors.length === 0;
    };

    // Update form validation when dependencies change
    useEffect(() => {
        setIsFormValid(validateForm());
    }, [formData, selectedCustomer, transactionStartTime, commission, jobStatsFile, selectedCryptoCurrency]);

    // Function to fetch crypto currencies from actual API
    const fetchCryptoCurrencies = async () => {
        setLoading(prev => ({ ...prev, cryptoCurrencies: true }));
        try {
            const response = await getCryptoCurrenciesAPI();
            console.log("Fetched cryptocurrencies:", response);
            
            if (response && response.data) {
                setCryptoCurrencies(response.data);
                
                // Set default selected crypto to BTC if available
                const defaultCrypto = response.data.find(crypto => crypto.symbol === "BTC");
                setSelectedCryptoCurrency(defaultCrypto || response.data[0]);
                
                if (defaultCrypto) {
                    setFormData(prev => ({ ...prev, cryptoType: defaultCrypto.rateBoxLabel }));
                }
            } else {
                throw new Error("Invalid response format");
            }
            
        } catch (error) {
            console.error("Error fetching cryptocurrencies:", error);
            // Fallback to mock data on error
            setCryptoCurrencies(mockCryptoCurrencies);
            const defaultCrypto = mockCryptoCurrencies.find(crypto => crypto.symbol === "BTC");
            setSelectedCryptoCurrency(defaultCrypto || mockCryptoCurrencies[0]);
            
            setSnackbarMessage("Using cached cryptocurrency data due to connection issue.");
            setSnackbarSeverity("warning");
            setSnackbarOpen(true);
        } finally {
            setLoading(prev => ({ ...prev, cryptoCurrencies: false }));
        }
    };

    // Fetch crypto currencies on component mount
    useEffect(() => {
        fetchCryptoCurrencies();
    }, []);

    // Update selected crypto when cryptoType changes
    useEffect(() => {
        if (cryptoCurrencies.length > 0 && formData.cryptoType) {
            const crypto = cryptoCurrencies.find(c => c.rateBoxLabel === formData.cryptoType);
            setSelectedCryptoCurrency(crypto || null);
            
            // Reset cryptoValue when crypto type changes to one without hasRateBox
            if (crypto && !hasRateBoxEffectively(crypto)) {
                setFormData(prev => ({ ...prev, cryptoValue: "" }));
            }
        }
    }, [formData.cryptoType, cryptoCurrencies]);

    const fetchExternalProcessors = async () => {
        setLoading(prev => ({ ...prev, externalProcessors: true }));
        try {
            const response = await getExternalProcessorsAPI();
            setExternalProcessors(response.data);
        } catch (error) {
            console.error("Error fetching external processors:", error);
            setSnackbarMessage("Failed to load external processors.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setLoading(prev => ({ ...prev, externalProcessors: false }));
        }
    };

    useEffect(() => {
        fetchExternalProcessors();
    }, []);

    const fetchCustomerBanks = async (customerId) => {
        if (!customerId) return [];
        try {
            const res = await getCustomerBanks(customerId);
            if (res.status) {
                return res.data.data;
            } else {
                setSnackbarMessage(res.message || "No saved banks found for this customer.");
                setSnackbarSeverity("warning");
                setSnackbarOpen(true);
            }
        } catch (error) {
            console.error("Error fetching customer banks:", error);
            setSnackbarMessage("An error occurred while fetching customer banks.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        }
        return [];
    };

    useEffect(() => {
        const verifyAccount = async () => {
            if (formData.customer_bank_code && formData.customer_account_number.length === 10) {
                setVerifyingAccount(true);
                try {
                    const res = await verifyBankAccount(formData.customer_account_number, formData.customer_bank_code);
                    if (res) {
                        setFormData(prev => ({ ...prev, customer_account_name: String(res) }));
                    } else {
                        setSnackbarMessage("Could not verify account name.");
                        setSnackbarSeverity("error");
                        setSnackbarOpen(true);
                        setFormData(prev => ({ ...prev, customer_account_name: "Verification Failed" }));
                    }
                } catch (error) {
                    console.error("Error verifying account:", error);
                    setSnackbarMessage("An error occurred while verifying the account.");
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                    setFormData(prev => ({ ...prev, customer_account_name: "" }));
                } finally {
                    setVerifyingAccount(false);
                }
            }
        };
        verifyAccount();
    }, [formData.customer_bank_code, formData.customer_account_number]);

    useEffect(() => {
        const amount = parseFloat(formData.cryptoAmount) || 0;
        const sRate = parseFloat(formData.sellingRate) || 0;
        const cRate = parseFloat(formData.costRate) || 0;

        const sp = amount * sRate;
        const cp = amount * cRate;
        
        setSellingPrice(sp);
        setCostPrice(cp);
        setCommission(cp - sp);
    }, [formData.cryptoAmount, formData.sellingRate, formData.costRate]);

    const fetchCustomers = async () => {
        try {
            setLoading((prev) => ({ ...prev, customers: true }));
            const response = await searchCustomersLive();
            console.log("Fetched customers:", response.data);
            setAllCustomers(response.data);
            setCustomersFetched(true);
        } catch (error) {
            console.error("Error searching customers:", error);
            setSnackbarMessage("Failed to search customers.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setLoading((prev) => ({ ...prev, customers: false }));
        }
    };

    const handleAddCustomer = async () => {
        setLoading(prev => ({...prev, customers: true}));
        console.log("Adding new customer:", newCustomer);
        try {
          const response = await createCustomer(newCustomer);
          
          // Refresh the customers list
          await fetchCustomers();
          
          // Search for the newly added customer to get full data with ID
          try {
            const searchResult = await searchCustomersLive();
            const customers = searchResult.data || [];
            
            // Find the newly added customer by phone or email
            const newCustomerAdded = customers.find(customer => 
              customer.phone === newCustomer.phone || 
              customer.email === newCustomer.email ||
              customer.altEmail === newCustomer.altEmail
            );
            
            if (newCustomerAdded) {
              setSelectedCustomer(newCustomerAdded);
              
              // CORRECTED FIELD MAPPING:
              // email field → username (Goldenpay App Username)
              // altEmail field → customerEmail (Customer Email)
              setFormData(prev => ({
                ...prev,
                customerName: `${newCustomerAdded.firstName} ${newCustomerAdded.lastName}`,
                customerPhone: newCustomerAdded.phone,
                customerEmail: newCustomerAdded.altEmail, // Customer Email from altEmail
                username: newCustomerAdded.email || newCustomerAdded.uname, // Goldenpay App Username from email
              }));
              
              // Also fetch the customer's banks if available
              if (newCustomerAdded.user_id) {
                const banks = await fetchCustomerBanks(newCustomerAdded.user_id);
                setCustomerBanks(banks);
              }
            } else {
              // Fallback to the response data if not found in search
              setSelectedCustomer(response.data);
              setFormData(prev => ({
                ...prev,
                customerName: `${response.data.firstName} ${response.data.lastName}`,
                customerPhone: response.data.phone,
                customerEmail: response.data.altEmail, // Customer Email from altEmail
                username: response.data.email || response.data.uname, // Goldenpay App Username from email
              }));
            }
          } catch (searchError) {
            console.error("Error searching for new customer:", searchError);
            // Fallback to response data
            setSelectedCustomer(response.data);
            setFormData(prev => ({
              ...prev,
              customerName: `${response.data.firstName} ${response.data.lastName}`,
              customerPhone: response.data.phone,
              customerEmail: response.data.altEmail, // Customer Email from altEmail
              username: response.data.email || response.data.uname, // Goldenpay App Username from email
            }));
          }
          
          setShowCustomerDialog(false);
          setNewCustomer({
            firstName: "",
            middleName: "",
            lastName: "",
            phone: "",
            email: "",
            grade: "A",
            altEmail: "",
          });
          
          setSnackbarMessage("Customer added and selected successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        } catch (error) {
          console.error("Error creating customer:", error);
          const errorMessage =
            error.response?.data?.message ||
            "Failed to add customer. Please check inputs.";
          setSnackbarMessage(errorMessage);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        } finally {
          setLoading(prev => ({...prev, customers: false}));
        }
      };

    useEffect(() => {
        const loadBanks = async () => {
            if (selectedCustomer) {
                // CORRECTED FIELD MAPPING:
                // email field → username (Goldenpay App Username)
                // altEmail field → customerEmail (Customer Email)
                setFormData(prev => ({
                    ...prev,
                    customerName: `${selectedCustomer.firstName} ${selectedCustomer.lastName}`,
                    customerPhone: selectedCustomer.phone,
                    customerEmail: selectedCustomer.altEmail, // Customer Email from altEmail
                    username: selectedCustomer.email || selectedCustomer.uname, // Goldenpay App Username from email
                }));
                const banks = await fetchCustomerBanks(selectedCustomer.user_id);
                setCustomerBanks(banks);
                setSelectedBankId(""); 
            } else {
                setFormData(prev => ({
                    ...prev,
                    customerName: '',
                    customerPhone: '',
                    customerEmail: '',
                    username: '',
                    customer_bank_code: '',
                    customer_account_number: '',
                    customer_account_name: '',
                }));
                setCustomerBanks([]);
                setSelectedBankId("");
            }
        };
        loadBanks();
    }, [selectedCustomer]);

    useEffect(() => {
        if (selectedBankId) {
            const selectedBank = customerBanks?.find(bank => bank.id === selectedBankId);
            if (selectedBank) {
                setFormData(prev => ({
                    ...prev,
                    customer_bank_code: selectedBank.customer_bank_code,
                    customer_account_number: selectedBank.customer_account_number,
                    customer_account_name: String(selectedBank.customer_account_name || ''),
                }));
            }
        }
    }, [selectedBankId, customerBanks]);
  
    const handleChange = (field: string, value: any) => {
      setFormData({ ...formData, [field]: value });
    };
  
    const handleCreateOrder = () => {
        if (!selectedCustomer) {
            setSnackbarMessage("Please select a customer before submitting.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            return;
        }
        setShowSummaryDialog(true);
    };

    const executeCreateOrder = async () => {
        setProcessingOrder(true);
        setShowConfirmDialog(false);
        
        const saleData = new FormData();
        saleData.append("customer_id", selectedCustomer?.user_id || "");
        saleData.append("customerName", formData.customerName);
        saleData.append("customerPhone", formData.customerPhone);
        saleData.append("customerEmail", formData.customerEmail);
        saleData.append("username", formData.username);
        saleData.append("cryptoType", formData.cryptoType);
        saleData.append("cryptoAmount", formData.cryptoAmount);
        saleData.append("cryptoValue", formData.cryptoValue || ""); // Add cryptoValue to form data
        saleData.append("sellingRate", formData.sellingRate);
        saleData.append("costRate", formData.costRate);
        saleData.append("transactionStartTime", transactionStartTime ? transactionStartTime.toISOString() : new Date().toISOString());
        saleData.append("customer_bank_code", formData.customer_bank_code);
        saleData.append("customer_account_number", formData.customer_account_number);
        saleData.append("customer_account_name", String(formData.customer_account_name || ''));
        saleData.append("external_processor", formData.external_processor);
        saleData.append("sellingPrice", sellingPrice.toString());
        saleData.append("costPrice", costPrice.toString());
        saleData.append("commission", commission.toString());

        if (uploadedFile) {
            saleData.append("file", uploadedFile);
        }
        if (jobStatsFile) {
            saleData.append("job_start_file", jobStatsFile);
        }
    
        try {
          const res = await createCustomerOrder_Crypto(Object.fromEntries(saleData.entries()));

          console.log({ res });
        
          setSnackbarMessage("Order created successfully!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);

         history.push('/folder/pending_raise_receipt_giftcard_crypto/move', { customer: selectedCustomer });
          setFormData(initialFormData);
          setSelectedCustomer(null);
          setUploadedFile(null);
          setJobStatsFile(null);
          setShowSummaryDialog(false);
        } catch (error) {
          console.error("Error creating order:", error);
          const errorMessage = error.response?.data?.message || "Failed to create order. Please try again.";
          setSnackbarMessage(errorMessage);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        } finally {
          setProcessingOrder(false);
        }
      };
  
    const formatWithCommas = (num: number) => {
        if (isNaN(num)) return "0.00";
        return num.toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') {
        return;
      }
      setSnackbarOpen(false);
    };

    // Helper function to get placeholder text based on selected crypto
    const getCryptoValuePlaceholder = () => {
        if (!selectedCryptoCurrency) return "Enter crypto value";
        return `e.g., 0.001 ${selectedCryptoCurrency.rateBoxLabel || selectedCryptoCurrency.symbol}`;
    };

    // Helper function to get label for crypto value field
    const getCryptoValueLabel = () => {
        if (!selectedCryptoCurrency) return "Crypto Value";
        return `Crypto Value (${selectedCryptoCurrency.rateBoxLabel || selectedCryptoCurrency.symbol})`;
    };
  
    return (
    

      <IonPage className="pushDown">
        <IonHeader>
          <IonToolbar>
            <MenuButton /> {/* This adds the hamburger icon */}
            <IonTitle>Crypto Transaction</IonTitle>
          </IonToolbar>
        </IonHeader>
        <Box sx={{ p: 2, bgcolor: 'background.paper', borderBottom: '1px solid #e0e0e0' }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <Autocomplete
              sx={{ flexGrow: 1 }}
              options={allCustomers || []}
              getOptionLabel={(option) => 
                `${option?.firstName || ''} ${option?.lastName || ''} ${option?.phone ? `(${option.phone})` : ''}`
              }
              value={selectedCustomer}
              onOpen={() => {
                if (!customersFetched) {
                  fetchCustomers();
                }
              }}
              onChange={(event, newValue) => {
                setSelectedCustomer(newValue);
              }}
              loading={loading.customers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search for a customer"
                  variant="outlined"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading.customers ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={() => setShowCustomerDialog(true)}
              sx={{ height: '56px' }}
            >
              New
            </Button>
          </Box>
        </Box>
        <IonContent>
          <Box sx={{ 
            display: "flex", 
            flexDirection: { xs: 'column', md: 'row' }, 
            height: { xs: "auto", md: "100%" }, 
            bgcolor: "#f5f7fa" 
          }}>
            {/* Form Panel */}
            <Box sx={{ width: { xs: "100%", md: "70%" }, p: 2, overflowY: { xs: "visible", md: "auto" } }}>
              <Paper sx={{ p: 2, borderRadius: 3 }}>
               
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }}><Chip label="CUSTOMER INFO" /></Divider>
                  </Grid>
  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Customer Email (Optional)"
                      type="email"
                      value={formData.customerEmail}
                      onChange={(e) => handleChange("customerEmail", e.target.value)}
                      disabled={!!selectedCustomer}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Goldenpay App Username (Optional)"
                      value={formData.username}
                      onChange={(e) => handleChange("username", e.target.value)}
                      disabled={!!selectedCustomer}
                      helperText="Goldenpay application username"
                    />
                  </Grid>

                  <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="CUSTOMER BANK INFO" /></Divider></Grid>
                    <Grid item xs={12}>
                        <FormControl fullWidth disabled={!selectedCustomer || customerBanks.length === 0}>
                            <InputLabel>Select Saved Bank Account</InputLabel>
                            <Select
                                value={selectedBankId}
                                label="Select Saved Bank Account"
                                onChange={(e) => setSelectedBankId(e.target.value as string)}
                            >
                                <MenuItem value=""><em>-- Select a bank --</em></MenuItem>
                                {customerBanks.map((bank) => (
                                    <MenuItem key={bank.id} value={bank.id}>
                                        {`${bank.customer_account_name} - ${bank.customer_account_number}`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth disabled={!!selectedBankId}>
                      <InputLabel>Customer Bank Name</InputLabel>
                      <Select
                        value={formData.customer_bank_code}
                        label="Customer Bank Name"
                        onChange={(e) => handleChange("customer_bank_code", e.target.value)}
                      >
                        {banks.map((bank) => (
                          <MenuItem key={bank.code} value={bank.code}>
                            {bank.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Customer Account Number"
                      type="number"
                      onWheel={(e) => (e.target as HTMLInputElement).blur()} 
                      sx={{
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        }
                      }}
                      value={formData.customer_account_number}
                      onChange={(e) => handleChange("customer_account_number", e.target.value)}
                      inputProps={{ maxLength: 10 }}
                      disabled={!!selectedBankId}
                      InputLabelProps={{ shrink: !!formData.customer_account_number }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Customer Account Name"
                      value={formData.customer_account_name}
                      disabled
                      InputProps={{
                        endAdornment: verifyingAccount && (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="TRANSACTION DETAILS" /></Divider></Grid>
                  
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Crypto Type</InputLabel>
                      <Select
                        value={formData.cryptoType}
                        label="Crypto Type"
                        onChange={(e) => handleChange("cryptoType", e.target.value)}
                        disabled={loading.cryptoCurrencies}
                        startAdornment={
                          loading.cryptoCurrencies ? (
                            <InputAdornment position="start">
                              <CircularProgress size={20} />
                            </InputAdornment>
                          ) : null
                        }
                      >
                        <MenuItem value="">
                          <em>-- Select Crypto --</em>
                        </MenuItem>
                        {cryptoCurrencies.map((crypto) => (
                          <MenuItem key={crypto.id} value={crypto.rateBoxLabel}>
                            {crypto.name} ({crypto.rateBoxLabel})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Crypto Amount"
                      type="number"
                      onWheel={(e) => (e.target as HTMLInputElement).blur()} 
                      sx={{
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        }
                      }}
                      value={formData.cryptoAmount}
                      onChange={(e) => handleChange("cryptoAmount", e.target.value)}
                    />
                  </Grid>
                  
                  {/* Conditionally render Crypto Value field based on hasRateBox */}
                  {hasRateBoxEffectively(selectedCryptoCurrency) && (
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={getCryptoValueLabel()}
                        type="number"
                        onWheel={(e) => (e.target as HTMLInputElement).blur()} 
                        sx={{
                          '& input[type=number]': {
                            MozAppearance: 'textfield',
                          },
                          '& input[type=number]::-webkit-outer-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          },
                          '& input[type=number]::-webkit-inner-spin-button': {
                            WebkitAppearance: 'none',
                            margin: 0,
                          }
                        }}
                        value={formData.cryptoValue}
                        onChange={(e) => handleChange("cryptoValue", e.target.value)}
                        placeholder={getCryptoValuePlaceholder()}
                        helperText={`Enter the actual ${selectedCryptoCurrency.name} value`}
                      />
                    </Grid>
                  )}
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Cost Rate "
                      type="number"
                      onWheel={(e) => (e.target as HTMLInputElement).blur()} 
                      sx={{
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        }
                      }}
                      value={formData.costRate}
                      onChange={(e) => handleChange("costRate", e.target.value)}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Crypto Rate (Selling Rate)"
                      type="number"
                      onWheel={(e) => (e.target as HTMLInputElement).blur()} 
                      sx={{
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        }
                      }}
                      value={formData.sellingRate}
                      onChange={(e) => handleChange("sellingRate", e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={12}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{ height: "56px" }}
                      >
                        Upload Evidence of Manual Payments (Optional)
                        <input
                          type="file"
                          hidden
                          onChange={(e) => setUploadedFile(e.target.files ? e.target.files[0] : null)}
                        />
                      </Button>
                      {uploadedFile && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {uploadedFile.name}
                        </Typography>
                      )}
                  </Grid>
                
                  <Grid item xs={12}><Divider sx={{ my: 1 }}><Chip label="TRANSACTION TIME" /></Divider></Grid>
                  <Grid item xs={12} md={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DateTimePicker
                        renderInput={(props) => <TextField {...props} fullWidth />}
                        label="Transaction Start Time"
                        value={transactionStartTime}
                        onChange={(newValue) => setTransactionStartTime(newValue)}
                      />
                    </LocalizationProvider>
                  </Grid>
                    <Grid item xs={12} md={6}>
                      <Button
                        variant="outlined"
                        component="label"
                        fullWidth
                        sx={{ height: "56px" }}
                      >
                        Upload Evidence of Job Start *
                        <input
                          type="file"
                          hidden
                          onChange={(e) => setJobStatsFile(e.target.files ? e.target.files[0] : null)}
                        />
                      </Button>
                      {jobStatsFile && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {jobStatsFile.name}
                        </Typography>
                      )}
                  </Grid>
                </Grid>
              </Paper>
            </Box>
  
            {/* Summary Panel */}
            <Box sx={{ 
              width: { xs: "100%", md: "30%" }, 
              p: 3, 
              bgcolor: "background.paper", 
              borderLeft: { xs: "none", md: "1px solid #e0e0e0" },
              borderTop: { xs: "1px solid #e0e0e0", md: "none" }
            }}>
              <Card variant="outlined" sx={{ borderRadius: 3, position: { xs: 'static', md: 'sticky' }, top: 20 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Transaction Summary
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography color="text.secondary">Cost Price (CP):</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "primary.main" }}>
                      ₦{formatWithCommas(costPrice)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography color="text.secondary">Selling Price (SP):</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: "secondary.main" }}>
                      ₦{formatWithCommas(sellingPrice)}
                    </Typography>
                  </Box>
                  
                  {/* Show Crypto Value in summary if available */}
                  {hasRateBoxEffectively(selectedCryptoCurrency) && formData.cryptoValue && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography color="text.secondary">Crypto Value:</Typography>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: "info.main" }}>
                        {formData.cryptoValue} {selectedCryptoCurrency.rateBoxLabel || selectedCryptoCurrency.symbol}
                      </Typography>
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography color="text.secondary" sx={{fontWeight: 600}}>Commission / Profit:</Typography>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700, 
                      color: commission >= 100 ? "success.main" : "error.main" 
                    }}>
                      ₦{formatWithCommas(commission)}
                      {commission < 100 && (
                        <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                          (Minimum ₦100 required)
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  
                  {/* Show specific validation errors */}
                  {formErrors.length > 0 && (
                    <Box sx={{ mb: 2, p: 1, bgcolor: 'error.light', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: 'white' }}>
                        Please fix the following:
                      </Typography>
                      {formErrors.slice(0, 3).map((error, index) => (
                        <Typography 
                          key={index} 
                          variant="body2" 
                          sx={{ 
                            ml: 1, 
                            fontWeight: 600,
                            color: 'white' 
                          }}
                        >
                          • {error}
                        </Typography>
                      ))}
                      {formErrors.length > 3 && (
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            ml: 1, 
                            fontWeight: 600,
                            color: 'white' 
                          }}
                        >
                          • and {formErrors.length - 3} more...
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 2 }} />
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleCreateOrder}
                    startIcon={processingOrder ? <CircularProgress size={24} color="inherit" /> : <Receipt />}
                    sx={{ 
                      py: 1.5, 
                      borderRadius: 2, 
                      textTransform: 'none', 
                      fontWeight: 600,
                      opacity: isFormValid && commission >= 100 ? 1 : 0.6
                    }}
                    disabled={!isFormValid || processingOrder || commission < 100}
                  >
                    {processingOrder ? 'Submitting...' : 'Submit Transaction'}
                  </Button>

                  {(!isFormValid || commission < 100) && (
                    <Typography variant="body2" color="error" sx={{ mt: 2, textAlign: 'center' }}>
                      {commission < 100 ? 
                        "Commission must be at least ₦100 to process transaction" : 
                        "Please fill all required fields to enable submission"}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
          </Box>
          
        {/* Customer Dialog */}
        <Dialog open={showCustomerDialog} onClose={() => setShowCustomerDialog(false)} maxWidth="sm" fullWidth style={{ zIndex: 2000 }}>
            <DialogTitle>
                Add New Customer
                <IconButton
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                    onClick={() => setShowCustomerDialog(false)}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Full Name(s)"
                            value={newCustomer.firstName}
                            onChange={(e) =>
                                setNewCustomer({
                                    ...newCustomer,
                                    firstName: e.target.value,
                                })
                            }
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            value={newCustomer.phone}
                            onChange={(e) =>
                                setNewCustomer({ ...newCustomer, phone: e.target.value })
                            }
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Goldenpay App Username (Optional)"
                            value={newCustomer.email}
                            onChange={(e) =>
                                setNewCustomer({ ...newCustomer, email: e.target.value })
                            }
                            helperText="Goldenpay application username"
                        />
                    </Grid>

                    <Grid item xs={12} md={12}>
                        <TextField
                            fullWidth
                            label="Customer Email (Optional)"
                            value={newCustomer.altEmail}
                            onChange={(e) =>
                                setNewCustomer({ ...newCustomer, altEmail: e.target.value })
                            }
                            type="email"
                            helperText="Customer's email address"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowCustomerDialog(false)}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleAddCustomer}
                    disabled={loading.customers || !newCustomer.firstName || !newCustomer.phone}
                >
                    {loading.customers ? <CircularProgress size={24} /> : "Add Customer"}
                </Button>
            </DialogActions>
        </Dialog>

 <Dialog open={showSummaryDialog} onClose={() => setShowSummaryDialog(false)} maxWidth="md" fullWidth>
    <DialogTitle sx={{ background: '#1976d2', color: 'white', py: 2, textAlign: 'center' }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
            <ReceiptLong />
            <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>Transaction Summary</Typography>
        </Box>
    </DialogTitle>
    <DialogContent dividers sx={{ p: 3 }}>
        <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Customer & Bank</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}><ListItemText primary="Customer Name" secondary={formData.customerName || 'N/A'} /></Grid>
                        <Grid item xs={12} sm={6}><ListItemText primary="Phone Number" secondary={formData.customerPhone || 'N/A'} /></Grid>
                        <Grid item xs={12}><ListItemText primary="Bank Name" secondary={banks.find(b => b.code === formData.customer_bank_code)?.name || 'N/A'} /></Grid>
                        <Grid item xs={12} sm={6}><ListItemText primary="Account Number" secondary={formData.customer_account_number || 'N/A'} /></Grid>
                        <Grid item xs={12} sm={6}><ListItemText primary="Account Name" secondary={formData.customer_account_name || 'N/A'} /></Grid>
                        <Grid item xs={12} sm={6}><ListItemText primary="Transaction Start Time" secondary={transactionStartTime?.toLocaleString() || 'N/A'} /></Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Crypto & Rates</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}><ListItemText primary="Crypto Type" secondary={formData.cryptoType} /></Grid>
                        <Grid item xs={12} sm={6}><ListItemText primary="Crypto Amount" secondary={formData.cryptoAmount} /></Grid>
                        
                        {/* Show Crypto Value in summary dialog if available */}
                        {hasRateBoxEffectively(selectedCryptoCurrency) && formData.cryptoValue && (
                            <Grid item xs={12} sm={6}>
                                <ListItemText 
                                    primary={`Crypto Value (${selectedCryptoCurrency.rateBoxLabel || selectedCryptoCurrency.symbol})`} 
                                    secondary={formData.cryptoValue} 
                                />
                            </Grid>
                        )}
                        
                        <Grid item xs={12} sm={6}><ListItemText primary="Cost Rate" secondary={`₦${formData.costRate}`} /></Grid>
                        <Grid item xs={12} sm={6}><ListItemText primary="Selling Rate" secondary={`₦${formData.sellingRate}`} /></Grid>
                    </Grid>
                </Paper>
                
                {/* File Previews Section */}
                <Box sx={{ mt: 2 }}>
                    {uploadedFile && (
                        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AttachFile fontSize="small" />
                                Evidence of Manual Payments (Optional)
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                    {uploadedFile.name}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Visibility />}
                                    onClick={() => window.open(URL.createObjectURL(uploadedFile), '_blank')}
                                >
                                    View
                                </Button>
                            </Box>
                        </Paper>
                    )}
                    
                    {jobStatsFile && (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Description fontSize="small" />
                                Evidence of Job Start *
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Typography variant="body2" sx={{ flex: 1 }}>
                                    {jobStatsFile.name}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Visibility />}
                                    onClick={() => window.open(URL.createObjectURL(jobStatsFile), '_blank')}
                                >
                                    View
                                </Button>
                            </Box>
                        </Paper>
                    )}
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>Financial Summary</Typography>
                <Box sx={{ display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 2 }}>
                    <Paper elevation={2} sx={{ flex: 1, p: 2, borderRadius: 2, textAlign: 'center', bgcolor: 'error.50' }}>
                        <Typography variant="button" display="block" sx={{ color: 'error.dark' }}>Cost Price</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'error.dark' }}>₦{formatWithCommas(costPrice)}</Typography>
                    </Paper>
                    <Paper elevation={2} sx={{ flex: 1, p: 2, borderRadius: 2, textAlign: 'center', bgcolor: 'success.50' }}>
                        <Typography variant="button" display="block" sx={{ color: 'success.dark' }}>Selling Price</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.dark' }}>₦{formatWithCommas(sellingPrice)}</Typography>
                    </Paper>
                    <Paper elevation={2} sx={{ flex: 1, p: 2, borderRadius: 2, textAlign: 'center', bgcolor: commission >= 100 ? 'info.50' : 'warning.50' }}>
                        <Typography variant="button" display="block" sx={{ color: commission >= 100 ? 'info.dark' : 'warning.dark' }}>Commission</Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: commission >= 100 ? 'info.dark' : 'warning.dark' }}>
                            ₦{formatWithCommas(commission)}
                            {commission < 100 && (
                                <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                                    (Minimum ₦100 required)
                                </Typography>
                            )}
                        </Typography>
                    </Paper>
                </Box>
            </Grid>
        </Grid>
    </DialogContent>
    <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => setShowSummaryDialog(false)} variant="outlined" startIcon={<Cancel />}>Cancel</Button>
        <Button 
            onClick={() => { setShowSummaryDialog(false); setShowConfirmDialog(true); }} 
            variant="contained" 
            startIcon={<CheckCircle />} 
            disabled={!isFormValid || commission < 100}
        >
            Confirm Transaction
        </Button>
    </DialogActions>
</Dialog>

        <Dialog open={showConfirmDialog} onClose={() => setShowConfirmDialog(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogContent>
                <Typography>Are you sure all values are correct? This action cannot be undone.</Typography>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
                    Crypto Type: {formData.cryptoType}
                </Typography>
                {hasRateBoxEffectively(selectedCryptoCurrency) && formData.cryptoValue && (
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Crypto Value: {formData.cryptoValue} {selectedCryptoCurrency.rateBoxLabel || selectedCryptoCurrency.symbol}
                    </Typography>
                )}
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    Job Starts File: {jobStatsFile ? 'Uploaded' : 'Not Uploaded'}
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                    Manual Payments File: {uploadedFile ? 'Uploaded (Optional)' : 'Not Uploaded'}
                </Typography>
                <Typography 
                    variant="body2" 
                    sx={{ mt: 1, fontWeight: 'bold', color: commission < 100 ? 'error.main' : 'success.main' }}
                >
                    Commission: ₦{formatWithCommas(commission)} 
                    {commission < 100 && " (Minimum ₦100 required)"}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setShowConfirmDialog(false)}>No, go back</Button>
                <Button 
                    onClick={executeCreateOrder} 
                    variant="contained" 
                    color="primary" 
                    disabled={processingOrder || commission < 100}
                >
                    {processingOrder ? <CircularProgress size={24} /> : "Yes, create order"}
                </Button>
            </DialogActions>
        </Dialog>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: "100%" }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </IonContent>
      </IonPage>
         
    );
  };
  
  export default PosPage_Crypto;
